import {
  COGO_SURFACE_PROPOSAL_SCHEMA_VERSION,
  agentSurfaceScenarioIdSchema,
  cogoSurfaceProposalGuestOutputSchema,
  cogoSurfaceProposalSchema,
  isAgentSurfaceScenarioId,
  isPlainJsonObject,
  type AgentSurfaceProposalExpectation,
} from '@cogo/a2a-gateway/agent-surface-contract'

/**
 * Why a proposal did not become a surface -- in the refusing contract's words.
 *
 * The admission boundary lives in `@cogo/a2a-gateway/agent-surface-contract`
 * and it refuses by THROWING. A consumer that catches the throw and answers
 * `null` erases four different facts into one: the reader cannot tell an
 * admitted surface from a refused one, a refused one from an empty reply, or a
 * surface whose identity is outside the closed catalogue from a surface that
 * broke some other clause. This module keeps them apart.
 *
 * Nothing here widens admission. Every value still goes through
 * `parseCogoSurfaceProposalText`; this only reads the refusal that already
 * happened.
 *
 * The vocabulary is the contract's, not this file's:
 *   - the closed catalogue is `isAgentSurfaceScenarioId` / the schema the
 *     contract exports, never a list spelled here (gated: the module source
 *     must not contain any catalogue id);
 *   - the sentence shown to the reader is the contract's own thrown message or
 *     its own zod issue message, copied verbatim, never re-worded here.
 */

/**
 * Minimal structural view of the contract's zod schemas.
 *
 * Declared structurally so this module imports the CONTRACT and nothing else --
 * the refusal sentences below are the ones those schemas already carry, so
 * there is no second copy of them here to drift.
 */
type ContractValidator = {
  safeParse(
    value: unknown,
  ):
    | { success: true }
    | { success: false; error: { issues: readonly { message: string }[] } }
}

/**
 * `unknown-scenario` is the one reason that has to be nameable on its own: it
 * is the fact a generated (non-catalogue) surface produces, and the reader
 * must be able to say "Studio has no scenario by that id" instead of showing a
 * clause about node ids. Everything else the contract refuses is reported
 * under `contract` WITH the contract's sentence, so no reason is invented here
 * and none is lost.
 */
export type AgentSurfaceRefusalReason = 'unknown-scenario' | 'contract'

/**
 * Which side the refused value came from.
 *
 * A wrong reason is worse than a vague one. "The bot proposed a surface Studio
 * has no scenario for" and "Studio asked for a scenario it does not have" are
 * bugs in opposite halves of the system, and both arrive here as an id outside
 * the catalogue. Collapsing them sends the reader to the wrong half.
 */
export type AgentSurfaceRefusalOrigin = 'request' | 'reply'

export interface AgentSurfaceRefusal {
  reason: AgentSurfaceRefusalReason
  origin: AgentSurfaceRefusalOrigin
  /** The refusing contract's own sentence, verbatim. */
  detail: string
  /** The scenario id at issue, when one was named as a string. */
  scenarioId?: string
}

function firstIssueMessage(
  validator: ContractValidator,
  value: unknown,
): string | undefined {
  const result = validator.safeParse(value)
  if (result.success) return undefined
  return result.error.issues[0]?.message ?? ''
}

const scenarioIdValidator =
  agentSurfaceScenarioIdSchema as unknown as ContractValidator

const requestedSurfaceShape = (
  cogoSurfaceProposalGuestOutputSchema.shape.surface as unknown as {
    shape: { kind: ContractValidator; baseRevision: ContractValidator }
  }
).shape

/**
 * A string the contract's schema-version literal cannot be, but every free
 * string field in the proposal can. Used to tell the pinned key apart from the
 * merely-string ones.
 */
const NOT_THE_SCHEMA_VERSION = `${COGO_SURFACE_PROPOSAL_SCHEMA_VERSION}#not`

let cachedSchemaVersionKey: string | undefined

/**
 * The proposal key the contract pins to `COGO_SURFACE_PROPOSAL_SCHEMA_VERSION`,
 * found by asking each top-level key of the contract's own schema which one
 * accepts that literal and rejects everything else.
 *
 * The key name is never spelled here. Spelling it would make "is this even a
 * proposal?" a private opinion of this file, and a rename in the contract would
 * silently reclassify every proposal as "not a proposal" -- i.e. back to the
 * exact silence this module exists to end. Throws rather than guesses if the
 * contract ever stops pinning exactly one key.
 */
function proposalSchemaVersionKey(): string {
  if (cachedSchemaVersionKey !== undefined) return cachedSchemaVersionKey
  const shape = cogoSurfaceProposalSchema.shape as unknown as Record<
    string,
    ContractValidator
  >
  const pinned = Object.keys(shape).filter(
    (key) =>
      shape[key].safeParse(COGO_SURFACE_PROPOSAL_SCHEMA_VERSION).success &&
      !shape[key].safeParse(NOT_THE_SCHEMA_VERSION).success,
  )
  if (pinned.length !== 1) {
    throw new Error(
      `agent surface contract pins ${pinned.length} keys to its schema version, expected exactly 1`,
    )
  }
  cachedSchemaVersionKey = pinned[0]
  return cachedSchemaVersionKey
}

/**
 * Whether a candidate CLAIMS to be a surface proposal.
 *
 * The execution-result envelope and every non-surface node output travel in the
 * same artifact. They are not refusals -- they are not proposals -- and
 * reporting them would bury the real one under a zod complaint about the
 * envelope. A candidate claims to be a proposal exactly when it declares the
 * key the contract pins to its schema version; whether the value is right is
 * the contract's business, and a wrong version is then a refusal with the
 * contract's own words.
 */
export function declaresCogoSurfaceProposal(value: unknown): boolean {
  return (
    isPlainJsonObject(value) &&
    Object.prototype.hasOwnProperty.call(value, proposalSchemaVersionKey())
  )
}

/**
 * The metadata object the contract reads the scenario id out of
 * (`proposal.surface.cogoJson.meta` in `admitCogoSurfaceProposal`).
 *
 * `cogoJson` is `z.record(z.unknown())` in the contract, so this path cannot be
 * derived from the schema the way the schema-version key can. It is pinned
 * BEHAVIOURALLY instead: the gate feeds a proposal that differs from an
 * admitted one only at this path and requires the reading to come back
 * `unknown-scenario`. Read a different path and the reason degrades to
 * `contract` and the gate goes red.
 */
function surfaceMetadata(value: unknown): Record<string, unknown> | undefined {
  if (!isPlainJsonObject(value)) return undefined
  const surface = value.surface
  if (!isPlainJsonObject(surface)) return undefined
  const cogoJson = surface.cogoJson
  if (!isPlainJsonObject(cogoJson)) return undefined
  const meta = cogoJson.meta
  return isPlainJsonObject(meta) ? meta : undefined
}

const SCENARIO_ID_METADATA_KEY = 'agentSurfaceScenarioId'

function refusalDetail(error: unknown): string {
  if (error instanceof Error) return error.message
  return String(error)
}

/**
 * The refusal a REPLY earned, classified by re-running the contract's own
 * predicate on the field the contract reads.
 *
 * `isAgentSurfaceScenarioId` is asked here rather than the thrown text being
 * pattern-matched: an id outside the catalogue is refused no matter which
 * clause fires first, so naming it is never wrong about admissibility, and the
 * contract's actual sentence is carried in `detail` either way. A missing id is
 * the same fact as an unknown one -- that is how the contract itself treats it.
 */
export function agentSurfaceReplyRefusal(
  candidate: unknown,
  error: unknown,
): AgentSurfaceRefusal {
  const detail = refusalDetail(error)
  const meta = surfaceMetadata(candidate)
  if (meta === undefined) return { reason: 'contract', origin: 'reply', detail }
  const declared = meta[SCENARIO_ID_METADATA_KEY]
  const named = typeof declared === 'string' ? { scenarioId: declared } : {}
  if (!isAgentSurfaceScenarioId(declared)) {
    return { reason: 'unknown-scenario', origin: 'reply', detail, ...named }
  }
  return { reason: 'contract', origin: 'reply', detail, ...named }
}

/**
 * The refusal the REQUEST earned, or undefined when the caller's expectation is
 * one the contract can be held to.
 *
 * Every clause is the contract's own schema, so the sentences the reader sees
 * are the contract's and a change there arrives here without an edit.
 */
export function agentSurfaceRequestRefusal(
  expectation: AgentSurfaceProposalExpectation,
): AgentSurfaceRefusal | undefined {
  const requested = (
    isPlainJsonObject(expectation) ? expectation : {}
  ) as Record<string, unknown>

  const scenarioId = requested.scenarioId
  const scenarioMessage = firstIssueMessage(scenarioIdValidator, scenarioId)
  if (scenarioMessage !== undefined) {
    return {
      reason: 'unknown-scenario',
      origin: 'request',
      detail: scenarioMessage,
      ...(typeof scenarioId === 'string' ? { scenarioId } : {}),
    }
  }

  const kindMessage = firstIssueMessage(
    requestedSurfaceShape.kind,
    requested.surfaceKind,
  )
  if (kindMessage !== undefined) {
    return { reason: 'contract', origin: 'request', detail: kindMessage }
  }

  const revisionMessage = firstIssueMessage(
    requestedSurfaceShape.baseRevision,
    requested.baseRevision,
  )
  if (revisionMessage !== undefined) {
    return { reason: 'contract', origin: 'request', detail: revisionMessage }
  }

  return undefined
}
